import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type RefObject,
    type WheelEvent
} from "react";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import readNDJSONStream from "ndjson-readablestream";
import ReactMarkdown from "react-markdown";
import { X } from "lucide-react";
import { Button } from "../ui";

import styles from "./HandlingSituationsFlow.module.css";

import { getToken, useMsalAuth } from "../../authConfig";
import { chatApi, customSituationsStreamApi } from "../../api/api";
import { ChatAppRequest, ChatAppResponse, Project, VectorFields } from "../../api/models";
import { useProjects } from "../../contexts/ProjectContext";
import { AnswerError, AnswerLoading } from "../Answer";
import { QuestionInput } from "../QuestionInput";
import { UserChatMessage } from "../UserChatMessage";
import {
    decodeCustomSituations,
    type CustomSituationChip,
    type SelectedSituation,
    type SituationKey
} from "../../utils/parseCustomSituations";

interface HandlingSituationsFlowProps {
    onDismiss: () => void;
    projectId?: string;
}

type FlowStage = "idle" | "selectProject" | "loadingCustomSituations" | "selectSituation" | "generating" | "ready" | "error";
type MessageTuple = [user: string, response: string];
type FailedAction = "loadProjects" | "projectNotAccessible" | "generate" | null;

export const HandlingSituationsFlow = ({ onDismiss, projectId }: HandlingSituationsFlowProps) => {
    const { t, i18n } = useTranslation();
    const client = useMsalAuth ? useMsal().instance : undefined;
    const { projects, loadProjects, error: projectsError, isLoading: projectsLoading } = useProjects();
    const shouldLoadProjectsOnMount = useRef(projects.length === 0);

    const [stage, setStage] = useState<FlowStage>("idle");
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [selectedSituation, setSelectedSituation] = useState<SelectedSituation | null>(null);
    const [customSituations, setCustomSituations] = useState<CustomSituationChip[]>([]);
    const [messages, setMessages] = useState<MessageTuple[]>([]);
    const [streamedContent, setStreamedContent] = useState<string>("");
    const [sessionState, setSessionState] = useState<string | null>(null);
    const [pendingQuestion, setPendingQuestion] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [lastFailedAction, setLastFailedAction] = useState<FailedAction>(null);

    const projectChipsRowRef = useRef<HTMLDivElement | null>(null);
    const situationChipsRowRef = useRef<HTMLDivElement | null>(null);

    // H1/H2: bootstrap + projectId-change invariants
    const hasInitialized = useRef(false);
    const lastHandledProjectIdRef = useRef<string | undefined>(undefined);
    // H5: session-scoped cache + stale-request sequence for custom situations
    const customCache = useRef<Map<string, CustomSituationChip[]>>(new Map());
    const customLoadSeq = useRef(0);

    const availableProjects = useMemo(
        () => [...projects].sort((left, right) => left.name.localeCompare(right.name, i18n.language)),
        [projects, i18n.language]
    );

    // Single source of truth — label + focus live together. Merged from Axel's SITUATION_FOCUS_BY_KEY.
    const situations = useMemo(
        () =>
            [
                {
                    key: "activitiesOutings",
                    label: t("handlingFlow.situations.activitiesOutings"),
                    focus: "preparation, shared choices, pacing, transitions, sensory load."
                },
                {
                    key: "morningRoutines",
                    label: t("handlingFlow.situations.morningRoutines"),
                    focus: "wake-up pacing, step-by-step guidance, transition prompts."
                },
                {
                    key: "irritationEscalation",
                    label: t("handlingFlow.situations.irritationEscalation"),
                    focus: "de-escalation language, safety, triggers, calming sequence."
                },
                {
                    key: "meals",
                    label: t("handlingFlow.situations.meals"),
                    focus: "preferences, appetite support, hydration reminders, respectful prompts."
                },
                {
                    key: "eveningRoutines",
                    label: t("handlingFlow.situations.eveningRoutines"),
                    focus: "low-stimulation wind-down, predictable sequence, sleep readiness."
                },
                {
                    key: "hygienePersonalCare",
                    label: t("handlingFlow.situations.hygienePersonalCare"),
                    focus: "dignity, consent, privacy, reassurance."
                }
            ] as Array<{ key: SituationKey; label: string; focus: string }>,
        [t]
    );

    const resolveAuthToken = useCallback(async (): Promise<string | undefined> => {
        try {
            if (client) {
                return await getToken(client);
            }
            return "";
        } catch (authError) {
            console.error("Failed to resolve auth token:", authError);
            return "";
        }
    }, [client]);

    // H5: load custom situation chips. Language+projectId keyed cache, stale-safe via seq ref.
    const loadCustomSituations = useCallback(
        async (pid: string) => {
            const key = `${i18n.language}:${pid}`;
            const cached = customCache.current.get(key);
            if (cached !== undefined) {
                setCustomSituations(cached);
                setStage("selectSituation");
                return;
            }

            const seq = ++customLoadSeq.current;
            setStage("loadingCustomSituations");
            try {
                const token = await resolveAuthToken();
                const response = await customSituationsStreamApi(
                    { context: { overrides: { language: i18n.language, project_id: pid } } },
                    token
                );
                if (!response.body) {
                    throw new Error("No response body");
                }

                let raw = "";
                for await (const event of readNDJSONStream(response.body)) {
                    if (event["error"]) {
                        throw new Error(event["error"]);
                    }
                    if (event["delta"]?.content) {
                        raw += event["delta"].content;
                    }
                }
                if (seq !== customLoadSeq.current) return; // stale

                const chips = decodeCustomSituations(raw);
                customCache.current.set(key, chips);
                setCustomSituations(chips);
                setStage("selectSituation");
            } catch (err) {
                if (seq !== customLoadSeq.current) return; // stale
                console.error("Failed to load custom situations:", err);
                customCache.current.set(key, []); // cache empty so we don't re-spam
                setCustomSituations([]);
                setStage("selectSituation"); // silent degrade to 6 generic chips
            }
        },
        [i18n.language, resolveAuthToken]
    );

    // Effect A: fire loadProjects() exactly once on mount if context has no projects.
    useEffect(() => {
        if (shouldLoadProjectsOnMount.current) {
            shouldLoadProjectsOnMount.current = false;
            loadProjects();
        }
    }, [loadProjects]);

    // Effect C (must appear BEFORE Effect B in source order): reset flow state on projectId change.
    useEffect(() => {
        if (lastHandledProjectIdRef.current === projectId) return;
        lastHandledProjectIdRef.current = projectId;
        hasInitialized.current = false;
        setSelectedProject(null);
        setSelectedSituation(null);
        setCustomSituations([]);
        setMessages([]);
        setStreamedContent("");
        setSessionState(null);
        setPendingQuestion("");
        setStage("idle");
        setError(undefined);
        setLastFailedAction(null);
        // NOTE: we deliberately do NOT clear customCache — cross-project cache is a feature.
    }, [projectId]);

    // Effect B: initialize stage/selection exactly once, using isLoading to detect loading.
    useEffect(() => {
        if (hasInitialized.current) return;
        if (projectsLoading) return; // still loading — wait for next render

        if (projectsError && projects.length === 0) {
            hasInitialized.current = true;
            setError(new Error(projectsError));
            setLastFailedAction("loadProjects");
            setStage("error");
            return;
        }

        if (projectId) {
            const match = projects.find(p => p.id === projectId);
            if (match) {
                hasInitialized.current = true;
                setSelectedProject(match);
                void loadCustomSituations(match.id);
            } else {
                hasInitialized.current = true;
                setError(new Error("project_not_accessible"));
                setLastFailedAction("projectNotAccessible");
                setStage("error");
            }
        } else {
            hasInitialized.current = true;
            setStage("selectProject");
        }
    }, [projectId, projects, projectsError, projectsLoading, loadCustomSituations]);

    // Axel's prompt rewrite, minus web-search lines (product decision). Double-written lines preserved.
    const buildHandlingPrompt = useCallback(
        (situation: SelectedSituation | null) => {
            const projectText = selectedProject?.name || "the client";
            const endingLine = t("handlingFlow.askAnythingElse");
            const uiLanguage = i18n.language;

            let situationText = "General care situation support";
            let situationFocusLine = "general care-situation handling.";
            if (situation) {
                if (situation.kind === "general") {
                    const entry = situations.find(item => item.key === situation.key);
                    if (entry) {
                        situationText = entry.label;
                        situationFocusLine = entry.focus;
                    }
                } else {
                    const chip = customSituations.find(item => item.id === situation.id);
                    if (chip) {
                        situationText = chip.label;
                        situationFocusLine = chip.description || "situation-specific caregiver support.";
                    }
                }
            }

            return [
                "Follow this priority for sourcing advice:",
                "1. Use the client's project notes first. If advice comes from a specific note, cite that note.",
                "2. If project notes are insufficient, say so and state what information is missing.",
                "1. Use the client's project notes first. If advice comes from a specific note, cite that note.",
                "2. If project notes are insufficient, say so and state what information is missing.",

                `Client/project context: ${projectText}.`,
                `Current situation type: ${situationText}.`,

                "Your task is to provide practical, safe, non-clinical guidance for handling this care situation.",
                "Your task is to provide practical, safe, non-clinical guidance for handling this care situation.",

                "It is very important that this advice is accurate because it directly affects patient wellbeing and caregiver safety.",

                "Always adapt suggestions to the selected situation type and keep advice concrete.",
                "Always adapt suggestions to the selected situation type and keep advice concrete.",

                "Suggestions must be directly based on the project notes for this client if available.",

                "Keep answers concrete and laconic. Do not elaborate unnecessarily.",
                "Keep answers concrete and laconic. Do not elaborate unnecessarily.",
                "Keep the answerts as short as possible.",

                "If notes on a similar situation describe a solution that was resolved, give advice based on that note.",
                "Only give general answers if there is no relevant context in the project notes.",

                "Output format (semi-structured):",
                "1) Immediate steps (now) - 1 to 2 bullet points",
                "2) Avoid - 1 bullet point",
                "3) Prevent next time - 1 to 2 bullet points",
                "4) Example phrases - 1 to 2 short phrases caregivers can say",
                "5) Escalate when - 1 point: When to escalate to higher authority (e.g. supervisor, doctor, etc.).",
                "",

                "Only include a bullet point if it adds specific, actionable information. Do not pad sections with generic or obvious advice.",
                "Only include a bullet point if it adds specific, actionable information. Do not pad sections with generic or obvious advice.",

                `Situation-specific focus for "${situationText}":`,
                situationFocusLine,
                "",
                "Safety and scope:",
                "- Do not provide diagnosis, medication changes, or legal conclusions.",
                "- Do not provide diagnosis, medication changes, or legal conclusions.",
                "- If there is immediate risk of harm, state that staff must follow emergency protocol immediately.",
                "",
                "Reason briefly before answering. Do not deliberate longer than necessary.",
                "Reason briefly before answering. Do not deliberate longer than necessary.",
                "Language rules:",
                `- The language of the user is (${uiLanguage}, BCP 47). Always write everything in (${uiLanguage}, BCP 47).`,
                `- Also answer follow-up questions in (${uiLanguage}, BCP 47)`,
                `- Translate given titles such as "Immediate steps (now)", "Avoid" and so on into (${uiLanguage}, BCP 47).`,
                `- Write response in (${uiLanguage}, BCP 47) even if the notes are in a different language.`,
                `- End with a direct translation of exactly this sentence: ${endingLine}`
            ].join("\n");
        },
        [selectedProject?.name, t, i18n.language, situations, customSituations]
    );

    const handleHorizontalWheel = useCallback((event: WheelEvent<HTMLDivElement>, containerRef: RefObject<HTMLDivElement>) => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
            return;
        }

        if (container.scrollWidth <= container.clientWidth) {
            return;
        }

        event.preventDefault();
        container.scrollLeft += event.deltaY;
    }, []);

    const makeApiRequest = useCallback(
        async (question: string, explicitSituation: SelectedSituation | null = null) => {
            if (!selectedProject || !question.trim()) {
                return;
            }

            const activeSituation = explicitSituation ?? selectedSituation;

            setPendingQuestion(question);
            setError(undefined);
            setLastFailedAction(null);
            setIsLoading(true);
            setIsStreaming(true);
            setStreamedContent("");
            setStage("generating");

            const historyMessages = messages.flatMap(([user, assistant]) => [
                { role: "user", content: user },
                { role: "assistant", content: assistant }
            ]);

            const request: ChatAppRequest = {
                messages: [...historyMessages, { role: "user", content: question }],
                is_web_search: false,
                profile_data: selectedProject.profile,
                context: {
                    overrides: {
                        language: i18n.language,
                        project_id: selectedProject.id,
                        current_session_id: sessionState || undefined,
                        vector_fields: VectorFields.Embedding,
                        use_agentic_retrieval: false,
                        include_full_notes: true,
                        prompt_template: `>>>${buildHandlingPrompt(activeSituation)}`
                    }
                },
                session_state: sessionState
            };

            const normalizeContent = (value: unknown): string => {
                if (typeof value === "string") {
                    return value;
                }
                if (value === null || value === undefined) {
                    return "";
                }
                try {
                    return JSON.stringify(value);
                } catch {
                    return String(value);
                }
            };

            try {
                const token = await resolveAuthToken();
                const response = await chatApi(request, true, token);

                if (!response.body) {
                    throw new Error("No response body");
                }

                let fullResponse = "";
                let latestSessionState: string | null = sessionState;
                const askResponse: ChatAppResponse = {
                    message: { content: "", role: "assistant" },
                    delta: { content: "", role: "assistant" },
                    context: { data_points: [], followup_questions: null, thoughts: [] },
                    session_state: sessionState
                };

                for await (const event of readNDJSONStream(response.body)) {
                    if (event["error"]) {
                        throw new Error(event["error"]);
                    }

                    if (event["context"]) {
                        askResponse.context = { ...askResponse.context, ...event["context"] };
                    }

                    if (event["session_state"]) {
                        latestSessionState = event["session_state"];
                        askResponse.session_state = event["session_state"];
                    }

                    if (event["message"]) {
                        const normalized = normalizeContent(event["message"].content);
                        askResponse.message = { ...event["message"], content: normalized };
                        if (!fullResponse && normalized) {
                            setIsLoading(false);
                            fullResponse = normalized;
                            setStreamedContent(normalized);
                        }
                    } else if (event["delta"]?.content) {
                        setIsLoading(false);
                        fullResponse += event["delta"].content;
                        setStreamedContent(fullResponse);
                    }
                }

                const finalContent = fullResponse || normalizeContent(askResponse.message.content);
                setMessages(previous => [...previous, [question, finalContent]]);
                setStreamedContent("");
                setSessionState(typeof latestSessionState === "string" ? latestSessionState : null);
                setStage("ready");
            } catch (requestError) {
                setError(requestError);
                setLastFailedAction("generate");
                setStage("error");
            } finally {
                setIsLoading(false);
                setIsStreaming(false);
            }
        },
        [buildHandlingPrompt, i18n.language, messages, resolveAuthToken, selectedProject, selectedSituation, sessionState]
    );

    const handleProjectSelected = useCallback(
        (project: Project) => {
            setSelectedProject(project);
            setError(undefined);
            void loadCustomSituations(project.id);
        },
        [loadCustomSituations]
    );

    const handleGeneralSituationSelected = useCallback(
        (situationKey: SituationKey, label: string) => {
            const selection: SelectedSituation = { kind: "general", key: situationKey };
            setSelectedSituation(selection);
            void makeApiRequest(label, selection);
        },
        [makeApiRequest]
    );

    const handleCustomSituationSelected = useCallback(
        (chip: CustomSituationChip) => {
            const selection: SelectedSituation = { kind: "custom", id: chip.id };
            setSelectedSituation(selection);
            void makeApiRequest(chip.label, selection);
        },
        [makeApiRequest]
    );

    const handleRetry = useCallback(() => {
        setError(undefined);

        if (lastFailedAction === "loadProjects" || lastFailedAction === "projectNotAccessible") {
            setStage("idle");
            setLastFailedAction(null);
            hasInitialized.current = false; // let Effect B re-run
            loadProjects();
            return;
        }

        if (lastFailedAction === "generate" && pendingQuestion) {
            void makeApiRequest(pendingQuestion);
            return;
        }

        setLastFailedAction(null);
        setStage(selectedProject ? "selectSituation" : "selectProject");
    }, [lastFailedAction, loadProjects, makeApiRequest, pendingQuestion, selectedProject]);

    const errorMessage = useMemo(() => {
        if (lastFailedAction === "projectNotAccessible") {
            return t("handlingFlow.errors.projectNotAccessible");
        }
        return String(error);
    }, [lastFailedAction, error, t]);

    const showProjectSelection = !projectId && stage === "selectProject";
    const showSelectedProjectMessage = !projectId && !!selectedProject && stage !== "selectProject";
    // Hide situation chips once generation starts so the response area stays unobstructed.
    const showSituationSelection = stage === "selectSituation" && !!selectedProject;
    // The bar is always mounted; before a project is chosen it is visible but disabled.
    const bottomInputDisabled = !selectedProject || isLoading || isStreaming || stage === "loadingCustomSituations";

    const isGeneralSelected = (key: SituationKey) =>
        !!selectedSituation && selectedSituation.kind === "general" && selectedSituation.key === key;
    const isCustomSelected = (id: string) =>
        !!selectedSituation && selectedSituation.kind === "custom" && selectedSituation.id === id;

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <Button variant="ghost" size="sm" leftIcon={<X size={18} />} onClick={onDismiss}>
                    {t("handlingFlow.dismiss")}
                </Button>

                <UserChatMessage message={t("handlingFlow.userLabel")} />

                {showProjectSelection && (
                    <>
                        <div className={styles.aiMessage}>{t("handlingFlow.selectProjectLabel")}</div>
                        {availableProjects.length > 0 ? (
                            <div
                                ref={projectChipsRowRef}
                                className={styles.projectChipsRow}
                                onWheel={event => handleHorizontalWheel(event, projectChipsRowRef)}
                            >
                                {availableProjects.map(project => {
                                    const isSelected = selectedProject?.id === project.id;

                                    return (
                                        <button
                                            key={project.id}
                                            type="button"
                                            className={`${styles.chip} ${isSelected ? styles.selected : ""}`}
                                            onClick={() => handleProjectSelected(project)}
                                            disabled={stage !== "selectProject"}
                                        >
                                            {project.name}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles.aiMessage}>{t("handlingFlow.noProjects")}</div>
                        )}
                    </>
                )}

                {showSelectedProjectMessage && <UserChatMessage message={selectedProject.name} />}

                {stage === "loadingCustomSituations" && (
                    <>
                        <div className={styles.answerWrapper}>
                            <AnswerLoading />
                            <p className={styles.loadingSubtitle}>{t("handlingFlow.loadingCustomSituations")}</p>
                        </div>
                    </>
                )}

                {showSituationSelection && (
                    <>
                        <div className={styles.aiMessage}>{t("handlingFlow.askSituationType")}</div>
                        <div
                            ref={situationChipsRowRef}
                            className={styles.situationChipsRow}
                            onWheel={event => handleHorizontalWheel(event, situationChipsRowRef)}
                        >
                            {situations.map(situation => (
                                <button
                                    key={situation.key}
                                    type="button"
                                    className={`${styles.chip} ${isGeneralSelected(situation.key) ? styles.selected : ""}`}
                                    onClick={() => handleGeneralSituationSelected(situation.key, situation.label)}
                                    disabled={isLoading || isStreaming}
                                >
                                    {situation.label}
                                </button>
                            ))}

                            {customSituations.length > 0 && (
                                <>
                                    <div className={styles.chipDivider} />
                                    {customSituations.map(chip => (
                                        <button
                                            key={chip.id}
                                            type="button"
                                            className={`${styles.chip} ${isCustomSelected(chip.id) ? styles.selected : ""}`}
                                            onClick={() => handleCustomSituationSelected(chip)}
                                            disabled={isLoading || isStreaming}
                                            title={chip.description || undefined}
                                        >
                                            {chip.label}
                                        </button>
                                    ))}
                                </>
                            )}
                        </div>
                    </>
                )}

                {messages.map(([question, response], index) => (
                    <div key={`${question}-${index}`}>
                        <UserChatMessage message={question} />
                        <div className={styles.answerWrapper}>
                            <div className={styles.answerContent}>
                                <ReactMarkdown>{response}</ReactMarkdown>
                            </div>
                        </div>
                    </div>
                ))}

                {stage === "idle" && (
                    <div className={styles.answerWrapper}>
                        <AnswerLoading />
                    </div>
                )}

                {stage === "generating" && pendingQuestion && (
                    <UserChatMessage message={pendingQuestion} />
                )}

                {stage === "generating" && (
                    <div className={styles.answerWrapper}>
                        {isStreaming && streamedContent ? (
                            <div className={styles.answerContent}>
                                <ReactMarkdown>{streamedContent}</ReactMarkdown>
                            </div>
                        ) : (
                            <AnswerLoading />
                        )}
                    </div>
                )}

                {stage === "error" && pendingQuestion && messages[messages.length - 1]?.[0] !== pendingQuestion && (
                    <UserChatMessage message={pendingQuestion} />
                )}

                {stage === "error" && (
                    <div className={styles.answerWrapper}>
                        <AnswerError error={errorMessage} onRetry={handleRetry} />
                    </div>
                )}
            </div>

            <div className={styles.chatInput}>
                <QuestionInput
                    clearOnSend
                    placeholder={t("handlingFlow.inputPlaceholder")}
                    disabled={bottomInputDisabled}
                    onSend={question => makeApiRequest(question)}
                    showSpeechInput={!!selectedProject}
                />
            </div>
        </div>
    );
};
